import React, { useEffect, useState } from 'react';
import { func, node, number, string } from 'prop-types';
import classNames from 'classnames';
import { Popover, getRenderable } from '@fiverr-private/fit';
import { isSeen, setSeen } from './utils/storage';
import { BASE_CLASS, BASE_CONTAINER_CLASS, BASE_BOX_CLASS, BASE_BOX_CONTENT_CLASS } from './constants';
import EducationBox from './EducationBox';

import './index.scss';

const isActive = ({ tooltipStorageKey, tooltipEndOfLife }) => {
    const hasBeenSeen = isSeen(tooltipStorageKey);
    const passedEndOfLife = tooltipEndOfLife ? Date.now() > tooltipEndOfLife : false;

    return !hasBeenSeen && !passedEndOfLife;
};

/**
 * @deprecated Please use EducationTooltip from @fiverr-private/tooltip instead.
 */
const EducationTooltip = ({
    className,
    containerClassName,
    boxClassName,
    boxContentClassName,
    title,
    body,
    badgeTitle,
    closeTitle,
    tooltipStorageKey,
    tooltipEndOfLife,
    onClose = Function.prototype,
    onActiveStateChanged = Function.prototype,
    children,
    ...props
}) => {
    const [active, setActive] = useState(isActive({ tooltipStorageKey, tooltipEndOfLife }));

    useEffect(() => {
        onActiveStateChanged(active);
    }, [active, onActiveStateChanged]);

    const handleClose = (event) => {
        setSeen(tooltipStorageKey);
        setActive(false);
        onClose(event);
    };

    const renderContent = () => {
        if (!active) {return null;}

        return (
            <EducationBox title={title}
                body={body}
                badgeTitle={badgeTitle}
                closeTitle={closeTitle}
                onClose={handleClose}/>
        );
    };

    const wrapperClasses = classNames(
        BASE_CLASS,
        className
    );

    const containerClasses = classNames(
        BASE_CONTAINER_CLASS,
        containerClassName
    );

    const boxClasses = classNames(
        BASE_BOX_CLASS,
        boxClassName,
        { active }
    );

    const boxContentClasses = classNames(
        BASE_BOX_CONTENT_CLASS,
        boxContentClassName
    );

    return (
        <Popover className={wrapperClasses}
            containerClassName={containerClasses}
            boxClassName={boxClasses}
            boxContentClassName={boxContentClasses}
            content={renderContent()}
            trigger={Popover.TRIGGERS.CLICK}
            position={Popover.POSITIONS.TOP}
            defaultOpen={active}
            onClose={handleClose}
            adjustPositionAfterMillis={500}
            {...props}>
            {getRenderable(children)}
        </Popover>
    );
};

EducationTooltip.displayName = 'EducationTooltip';

EducationTooltip.propTypes = {
    /**
     * Custom classes you wish to pass to the component.
     */
    className: string,
    /**
     * Custom classes you wish to pass to the container element. (orca-education-tooltip-container)
     */
    containerClassName: string,
    /**
     * Custom classes you wish to pass to the box element. (orca-education-tooltip-box)
     */
    boxClassName: string,
    /**
     * Custom classes you wish to pass to the box content element. (orca-education-tooltip-box-content)
     */
    boxContentClassName: string,
    /**
     * The presented tooltip title
     */
    title: node,
    /**
     * The presented tooltip body
     */
    body: node,
    /**
     * The presented tooltip badge title
     */
    badgeTitle: node,
    /**
     * The presented tooltip close button title
     */
    closeTitle: node,
    /**
     * An optional tooltip storage key if the tooltip visibility
     * should be manged using local storage
     */
    tooltipStorageKey: string,
    /**
     * An optional tooltip end of life date that if provided will
     * not show the tooltip after the provided date.
     */
    tooltipEndOfLife: number,
    /**
     * A callback function triggered when the tooltip is closed.
     * Receives one argument - the event that caused the close cycle.
     */
    onClose: func,
    /**
     * A callback function triggered when the active state was changed.
     */
    onActiveStateChanged: func,
    /**
     * The content that's being wrapped with the tooltip functionality.
     */
    children: node.isRequired
};

Object.assign(EducationTooltip, {
    POSITIONS: Popover.POSITIONS
});

export default EducationTooltip;
